import { Text } from '@byecode/ui'
import type { TypeInstanceMap } from '@lighthouse/core'
import { FilterSelectWay, FilterWay } from '@lighthouse/core'
import { emptyTextField, ListItem4ByecodeUi, SelectDataSource, useAtomData, VariableSelect } from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { FieldSelect } from '@/components/FieldSelect'
import { useCurrentPageContext } from '@/contexts/PageContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDsListAndFieldOptions } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import { DataSetting } from './DataSetting'
import { getFieldOptions } from './help'

interface TextFilterProps {
    prefixName: string
}

export const TextFilter: React.FC<TextFilterProps> = ({ prefixName }) => {
    const { control } = useFormContext()
    const currentAppId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const pointer = useWatch({ control, name: `${prefixName}.pointer` })
    // const fieldPointer = useWatch({ control, name: `${prefixName}.fieldPointer` })
    // const showFieldPointer = useWatch({ control, name: `${prefixName}.showFieldPointer` })
    const selectWay = useWatch({ control, name: `${prefixName}.selectWay` })

    const { dataSourceList, fieldOptions, dataSource } = useDsListAndFieldOptions(currentAppId, envId, pointer)
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()
    const { customViewOption } = useVariableCustomViewOption()

    const textFieldOptions = useMemo(
        () => getFieldOptions(fieldOptions, dataSource?.schema, FilterWay.textFilter),
        [dataSource, fieldOptions]
    )

    const defaultValueInnerTypes = useMemo<TypeInstanceMap[]>(
        () => (selectWay === FilterSelectWay.multiSelect ? ['ARRAY'] : ['TEXT']),
        [selectWay]
    )

    return (
        <>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>选项来源</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.pointer`}
                    render={({ field: { value, onChange } }) => {
                        return <SelectDataSource withinPortal size="lg" value={value} onChange={onChange} dataSourceList={dataSourceList} />
                    }}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>选项值</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.fieldPointer`}
                    render={({ field: { value, onChange } }) => {
                        return (
                            <FieldSelect
                                dsId={pointer}
                                style={{
                                    width: 180
                                }}
                                options={textFieldOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>显示为</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.showFieldPointer`}
                    render={({ field: { value, onChange } }) => {
                        return (
                            <FieldSelect
                                dsId={pointer}
                                style={{
                                    width: 180
                                }}
                                options={textFieldOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>默认值</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.textDefaultVariables`}
                    render={({ field: { value, onChange } }) => {
                        return (
                            <VariableSelect
                                style={{ width: 180 }}
                                width="100%"
                                placeholder="请选择变量"
                                innerTypes={defaultValueInnerTypes}
                                value={value}
                                onChange={onChange}
                                viewOptions={customViewOption ? [customViewOption] : undefined}
                                pageOptions={pageOptions}
                                userOption={userOption}
                                dataSourceList={dataSourceList}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>
            {dataSource && <DataSetting prefixName={prefixName} dataSource={dataSource} />}
        </>
    )
}
